import { useState } from 'react'
import type { EpisodeDetail, EpisodeStatus } from '../api/types'
import { usePublishEpisode, useUnpublishEpisode } from '../hooks/useShows'
import { formatDate } from '../utils/format'
import { Badge, Button, Card, CopyField, ErrorNote, Field } from './ui'

/** The iframe to paste into another site. */
export function embedCode(embedUrl: string): string {
  return `<iframe src="${embedUrl}" width="100%" height="180" frameborder="0" loading="lazy" allow="autoplay"></iframe>`
}

/** Published or scheduled, beside an episode's status. */
export function PublishedBadge({ publishedAt }: { publishedAt: string | null }) {
  if (!publishedAt) return null
  return Date.parse(publishedAt) <= Date.now() ? <Badge tone="ok">Published</Badge> : <Badge tone="accent">Scheduled</Badge>
}

/** Put the episode on its show page, feed and embed player, now or on a date. */
export function PublishCard({ workspaceId, episode, status, canEdit }: { workspaceId: string; episode: EpisodeDetail; status: EpisodeStatus; canEdit: boolean }) {
  const publish = usePublishEpisode(workspaceId, episode.id)
  const unpublish = useUnpublishEpisode(workspaceId, episode.id)
  const [when, setWhen] = useState('')
  const { publication } = episode
  const at = episode.published_at
  const ready = status === 'ready' || status === 'published'
  const error = publish.error ?? unpublish.error
  const original = publication.original_published_at
  const take = canEdit && (
    <Button variant="secondary" busy={unpublish.isPending} onClick={() => unpublish.mutate()}>
      Unpublish
    </Button>
  )
  return (
    <Card title="Publish">
      <div className="stack">
        {at && publication.members_only && ready && new Date(at) <= new Date() ? (
          <>
            <p className="row">
              <Badge tone="ok">Published to members</Badge>
              <span className="muted small">{formatDate(at)}</span>
            </p>
            <p className="muted small">
              This show is members-only, so the episode plays only on your own site, through the links its server asks for. It isn't on the
              public page, the feed or the embed player.
            </p>
            {take}
          </>
        ) : at && publication.live ? (
          <>
            <p className="row">
              <Badge tone="ok">Published</Badge>
              <span className="muted small">{formatDate(at)}</span>
            </p>
            <a href={publication.page_url} target="_blank" rel="noopener noreferrer">
              Open the public page ↗
            </a>
            <CopyField label="Embed on your site" value={embedCode(publication.embed_url)} />
            <CopyField label="Show feed" value={publication.feed_url} />
            {take}
          </>
        ) : at ? (
          <>
            <p className="row">
              <Badge tone="accent">Scheduled</Badge>
              <span className="muted small">for {formatDate(at, true)}</span>
            </p>
            <div className="row">
              {canEdit && (
                <Button busy={publish.isPending} onClick={() => publish.mutate({ published_at: new Date().toISOString() })}>
                  Publish now
                </Button>
              )}
              {take}
            </div>
          </>
        ) : !ready ? (
          <p className="muted">An episode can be published once processing has finished.</p>
        ) : canEdit ? (
          <form
            className="stack-sm"
            onSubmit={(event) => {
              event.preventDefault()
              publish.mutate({ published_at: when ? new Date(when).toISOString() : null })
            }}
          >
            <p className="muted small">
              Publishing puts the episode on the show's public page and in its podcast feed.
              {original && ` It was first published on ${formatDate(original)}; that date is kept unless you pick another.`}
            </p>
            <Field label="When" hint="Leave empty to publish now, or pick a date to schedule it.">
              <input type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} />
            </Field>
            <div className="row">
              <Button type="submit" busy={publish.isPending}>
                {when ? 'Schedule' : 'Publish'}
              </Button>
            </div>
          </form>
        ) : (
          <p className="muted">Not published.</p>
        )}
        {error && <ErrorNote error={error} />}
      </div>
    </Card>
  )
}
